// src/components/Catering/CreateInvoiceForm.js
import { toast } from "react-hot-toast";
import React, { useEffect, useState } from "react";
import { getCateringItems } from "../../services/cateringItemService";
import { createInvoice } from "../../services/cateringInvoiceService";
import { useNavigate } from "react-router-dom";

const CreateInvoiceForm = () => {
  const [masterItems, setMasterItems] = useState([]);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const navigate = useNavigate();

  const [customer, setCustomer] = useState({
    customerName: "",
    mobile: "",
    address: "",
    eventDate: "",
    budget: "",
    paidAmount: "",
  });

  const [selectedItemId, setSelectedItemId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [rate, setRate] = useState("");
  const [remarks, setRemarks] = useState("");

  useEffect(() => {
    getCateringItems().then(setMasterItems).catch(console.error);
  }, []);

  const addItem = () => {
    const selected = masterItems.find((i) => i.id === parseInt(selectedItemId));
    if (!selected || !quantity || !rate) return;

    // Prevent adding the same item again
    const alreadyExists = invoiceItems.find(
      (i) => i.itemName === selected.itemName
    );
    if (alreadyExists) {
      toast.error(
        "Item already added. You can update quantity by removing and re-adding."
      );
      return;
    }

    const total = parseFloat(rate) * parseFloat(quantity);

    setInvoiceItems((prev) => [
      ...prev,
      {
        itemName: selected.itemName,
        category: selected.category || "",
        quantity,
        rate,
        total,
        remarks,
      },
    ]);

    setSelectedItemId("");
    setQuantity("");
    setRate("");
    setRemarks("");
  };

  const removeItem = (index) => {
    setInvoiceItems((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSubmit = async () => {
    const payload = {
      ...customer,
      budget: parseFloat(customer.budget),
      paidAmount: parseFloat(customer.paidAmount),
      items: invoiceItems,
    };

    try {
      const res = await createInvoice(payload);
      toast.success("Invoice created successfully!");

      // Redirect to invoice list after success
      navigate("/catering-invoices");
    } catch (err) {
      toast.error("Failed to create invoice");
      console.error(err);
    }
  };

  const updateQuantity = (index, newQty) => {
    setInvoiceItems((prevItems) => {
      const updatedItems = [...prevItems];
      const item = updatedItems[index];

      const quantity = parseFloat(newQty) || 0;
      const total = quantity * parseFloat(item.rate);

      updatedItems[index] = {
        ...item,
        quantity,
        total,
      };

      return updatedItems;
    });
  };

  return (
    <div className="p-2 max-w-3xl mx-auto text-gray-800 bg-white shadow-md rounded-lg">
      <h2 className="text-xl font-bold mb-4">Create Catering Invoice</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block mb-1 text-sm font-medium">
            Customer Name
          </label>
          <input
            className="border p-2 rounded w-full"
            placeholder="Customer Name"
            value={customer.customerName}
            onChange={(e) =>
              setCustomer({ ...customer, customerName: e.target.value })
            }
          />
        </div>

        <div>
          <label className="block mb-1 text-sm font-medium">Mobile</label>
          <input
            className="border p-2 rounded w-full"
            placeholder="Mobile"
            value={customer.mobile}
            onChange={(e) =>
              setCustomer({ ...customer, mobile: e.target.value })
            }
          />
        </div>

        <div>
          <label className="block mb-1 text-sm font-medium">Address</label>
          <input
            className="border p-2 rounded w-full"
            placeholder="Address"
            value={customer.address}
            onChange={(e) =>
              setCustomer({ ...customer, address: e.target.value })
            }
          />
        </div>

        <div>
          <label className="block mb-1 text-sm font-medium">Event Date</label>
          <input
            className="border p-2 rounded w-full"
            type="date"
            value={customer.eventDate}
            onChange={(e) =>
              setCustomer({ ...customer, eventDate: e.target.value })
            }
          />
        </div>

        <div>
          <label className="block mb-1 text-sm font-medium">Total Budget</label>
          <input
            className="border p-2 rounded w-full"
            placeholder="Total Budget"
            type="number"
            value={customer.budget}
            onChange={(e) =>
              setCustomer({ ...customer, budget: e.target.value })
            }
          />
        </div>

        <div>
          <label className="block mb-1 text-sm font-medium">Paid Amount</label>
          <input
            className="border p-2 rounded w-full"
            placeholder="Paid Amount"
            type="number"
            value={customer.paidAmount}
            onChange={(e) =>
              setCustomer({ ...customer, paidAmount: e.target.value })
            }
          />
        </div>
      </div>

      <h3 className="text-lg font-semibold mt-6 mb-2">Add Items</h3>

      <div className="flex flex-wrap gap-2 mb-4">
        <div>
          <label className="block text-sm font-medium">Item</label>
          <select
            value={selectedItemId}
            onChange={(e) => setSelectedItemId(e.target.value)}
            className="border p-2 rounded w-[200px]"
          >
            <option value="">Select Item</option>
            {masterItems.map((i) => (
              <option key={i.id} value={i.id}>
                {i.itemName}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium">Quantity</label>
          <input
            placeholder="Quantity"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="border p-2 rounded w-[100px]"
          />
        </div>

        <div>
          <label className="block text-sm font-medium">Rate</label>
          <input
            placeholder="Rate"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            className="border p-2 rounded w-[100px]"
          />
        </div>

        <div>
          <label className="block text-sm font-medium">Remarks</label>
          <input
            placeholder="Remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            className="border p-2 rounded w-[150px]"
          />
        </div>

        <div className="flex items-end">
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded h-10"
            onClick={addItem}
          >
            Add Item
          </button>
        </div>
      </div>

      <table className="w-full border mt-4">
        <thead>
          <tr className="bg-gray-100">
            <th className="border px-2 py-1">#</th>
            <th className="border px-2 py-1">Item</th>
            <th className="border px-2 py-1">Qty</th>
            <th className="border px-2 py-1">Rate</th>
            <th className="border px-2 py-1">Total</th>
            <th className="border px-2 py-1">Remarks</th>
            <th className="border px-2 py-1">❌</th>
          </tr>
        </thead>
        <tbody>
          {invoiceItems.map((item, idx) => (
            <tr key={idx}>
              <td className="border px-2 py-1">{idx + 1}</td>
              <td className="border px-2 py-1">{item.itemName}</td>
              <td className="border px-2 py-1">
                <input
                  type="number"
                  min="1"
                  value={item.quantity}
                  onChange={(e) => updateQuantity(idx, e.target.value)}
                  className="w-16 border rounded px-1 text-center"
                />
              </td>
              <td className="border px-2 py-1">₹{item.rate}</td>
              <td className="border px-2 py-1">₹{item.total}</td>
              <td className="border px-2 py-1">{item.remarks || "-"}</td>
              <td className="border px-2 py-1 text-center">
                <button
                  onClick={() => removeItem(idx)}
                  className="text-red-500 hover:text-red-700 font-bold"
                  title="Remove"
                >
                  ×
                </button>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-gray-200 font-semibold">
            <td colSpan={4} className="border px-2 py-1 text-right">
              Total
            </td>
            <td className="border px-2 py-1">
              ₹{invoiceItems.reduce((sum, item) => sum + Number(item.total), 0)}
            </td>
            <td className="border px-2 py-1" colSpan={2}></td>
          </tr>
        </tfoot>
      </table>

      <button
        onClick={handleSubmit}
        className="mt-6 bg-green-600 text-white px-6 py-2 rounded shadow"
      >
        Submit Invoice
      </button>
    </div>
  );
};

export default CreateInvoiceForm;
// This component allows users to create a catering invoice by selecting items from a master list, entering customer details, and submitting the invoice.
// It uses the `getCateringItems` service to fetch available items and `createInvoice` service to submit the invoice data.
